
import { z } from 'zod';
import { validateContentSafety, validateMeditationContent } from './contentValidation';

// Enhanced rate limits with suspicious activity thresholds
export const ENHANCED_RATE_LIMITS = {
  CONTENT_GENERATION: {
    action: 'content_generation',
    maxRequests: 10,
    windowMinutes: 60,
    suspiciousThreshold: 8
  },
  AUDIO_GENERATION: {
    action: 'audio_generation',
    maxRequests: 5,
    windowMinutes: 60,
    suspiciousThreshold: 4
  },
  SESSION_SAVE: {
    action: 'session_save',
    maxRequests: 20,
    windowMinutes: 60,
    suspiciousThreshold: 15
  },
  PROFILE_UPDATE: {
    action: 'profile_update',
    maxRequests: 10,
    windowMinutes: 60,
    suspiciousThreshold: 8
  }
} as const;

// Enhanced content validation schemas
export const enhancedContentSchema = z.string()
  .min(1, 'Content cannot be empty')
  .max(10000, 'Content exceeds maximum length')
  .refine((content) => {
    const validation = validateContentSafety(content);
    return validation.isValid;
  }, 'Content contains unsafe patterns');

export const meditationContentSchema = z.object({
  content: z.string(),
  category: z.string()
}).refine((data) => {
  const validation = validateMeditationContent(data.content, data.category);
  return validation.isValid;
}, 'Content is not appropriate for meditation');

// File validation for audio uploads
export const audioFileSchema = z.object({
  size: z.number().max(52428800, 'File size must be less than 50MB'),
  type: z.string().refine((type) => {
    const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg'];
    return allowedTypes.includes(type);
  }, 'Invalid audio file type')
});

// Enhanced sanitization with content validation
export const sanitizeAndValidateInput = (input: string, maxLength: number = 1000): string => {
  if (!input || typeof input !== 'string') {
    throw new Error('Invalid input provided');
  }

  // Basic sanitization
  let sanitized = input.trim();
  
  // Remove potential XSS vectors
  sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  
  // Truncate to max length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  // Validate the sanitized content
  const validation = validateContentSafety(sanitized);
  if (!validation.isValid) {
    throw new Error(`Content validation failed: ${validation.errors.join(', ')}`);
  }

  return sanitized;
};

// Enhanced session validation
export const enhancedSessionTitleSchema = z.string()
  .min(1, 'Session title is required')
  .max(200, 'Session title is too long')
  .refine((title) => {
    try {
      sanitizeAndValidateInput(title, 200);
      return true;
    } catch {
      return false;
    }
  }, 'Session title contains invalid content');
